/**
 * Mode REST (server modules/modes/routes.ts): GET /mode, POST /mode/override. Members only: guests
 * and lodgers get 404 / 403, so `null` means "no presence for this viewer" (they keep the plain alarm text).
 * One shared view per home (the home screen's status line, the alarm sheet and the hallway tablet stay in step).
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import type { ModeOverrideInput, ModeView } from '@home/shared/modes';
import { store } from './store.js';

const base = (home: string): string => `/api/homes/${encodeURIComponent(home)}/mode`;

async function json<T>(r: Response): Promise<T | null> {
  return r.ok ? ((await r.json()) as T) : null;
}

/** Keyed by viewer + home, so a different sign-in never sees the previous viewer's presence. */
const key = (home: string): string => `${store.user?.id ?? ''}|${home}`;
const views = new Map<string, ModeView | null>();
const inflight = new Map<string, Promise<void>>();
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((fn) => fn());

function put(home: string, v: ModeView | null): void {
  views.set(key(home), v);
  emit();
}

/** Re-fetch a home's view (deduped while a request is in flight). Network errors keep the last view. */
export function refreshMode(home: string): Promise<void> {
  const k = key(home);
  const cur = inflight.get(k);
  if (cur) return cur;
  const p = fetch(base(home), { credentials: 'same-origin' })
    .then(async (r) => put(home, await json<ModeView>(r)))
    .catch(() => undefined)
    .finally(() => inflight.delete(k));
  inflight.set(k, p);
  return p;
}

async function post(home: string, path: string, body: unknown, what: string): Promise<ModeView> {
  const r = await fetch(`${base(home)}/${path}`, {
    method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
  });
  const v = await json<ModeView>(r);
  if (!v) throw new Error(`${what} failed (${r.status})`);
  put(home, v);
  return v;
}

export const postOverride = (home: string, body: ModeOverrideInput): Promise<ModeView> => post(home, 'override', body, 'Presence');

/** The home's mode view: fetched on mount, every 60 s and when the window regains focus. null = none / no access. */
export function useModeView(home: string | null | undefined): ModeView | null {
  const subscribe = useCallback((fn: () => void) => {
    listeners.add(fn);
    return () => void listeners.delete(fn);
  }, []);
  const view = useSyncExternalStore(subscribe, () => (home ? (views.get(key(home)) ?? null) : null));
  useEffect(() => {
    if (!home) return undefined;
    void refreshMode(home);
    const tick = (): void => void refreshMode(home);
    const id = window.setInterval(tick, 60_000);
    window.addEventListener('focus', tick);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', tick);
    };
  }, [home]);
  return view;
}
